import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL, STORAGE_KEYS, USER_ROLES } from '../constant/constant';

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 20000,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }, index) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Function to get stored tokens
const getStoredTokens = () => {
  try {
    const sessionToken = localStorage.getItem('sessionToken');
    const refreshToken = localStorage.getItem('refreshToken');

    return {
      sessionToken,
      refreshToken,
    };
  } catch (e) {
    return { sessionToken: null, refreshToken: null };
  }
};

// Function to clear tokens
const clearTokens = () => {
  try {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('refreshToken');
  } catch (e) {
    console.error(e);
  }
};

// Function to refresh token
const refreshSessionToken = async () => {
  const { refreshToken: storedRefreshToken } = getStoredTokens();

  if (!storedRefreshToken) {
    throw new Error('No refresh token available');
  }

  localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);

  try {
    const response = await axios.post(
      `${API_URL.BASE_AUTH}/rest/big-local/api/v1/auth/token/refresh?refreshToken=${storedRefreshToken}&role=${USER_ROLES.VENDOR}`
    );

    const { sessionToken: newSessionToken, refreshToken: newRefreshToken } = response.data;

    if (!newSessionToken) {
      throw new Error('Backend returned invalid response - no sessionToken');
    }

    // Store new tokens
    localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, newSessionToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);

    return newSessionToken;
  } catch (error) {
    // Don't call handleUnauthorizedError here — the caller (response interceptor) handles it
    clearTokens();
    throw error;
  }
};

// Request interceptor to add auth tokens
axiosInstance.interceptors.request.use(
  (config) => {
    const { sessionToken, refreshToken } = getStoredTokens();

    // Log request details
    const requestId = Math.random().toString(36).substr(2, 9);
    config.requestId = requestId;

    // Only add tokens if they exist and headers don't already have them
    if (sessionToken && !config.headers.sessionToken) {
      config.headers.sessionToken = sessionToken;
    }
    if (refreshToken && !config.headers.refreshToken) {
      config.headers.refreshToken = refreshToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors globally
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const requestId = error.config?.requestId || 'UNKNOWN';

    if (!error.response) {
      // No response from server (network error)
      if (error.code === 'ERR_CANCELED') {
        return error;
      }

      return Promise.reject({ message: 'No Internet Connection' });
    }

    const { status } = error.response;
    const originalRequest = error.config;

    if (status === 403 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              resolve(token);
            },
            reject: (err) => {
              reject(err);
            },
          });
        })
          .then((token) => {
            originalRequest.headers.sessionToken = token;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const newSessionToken = await refreshSessionToken();
        processQueue(null, newSessionToken);

        // Retry original request with new token
        originalRequest.headers.sessionToken = newSessionToken;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        handleUnauthorizedError();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Function to handle 403 Unauthorized error (when refresh fails)
const handleUnauthorizedError = async () => {
  // Read tokens and identifiers BEFORE clearing
  const sessionToken = localStorage.getItem('sessionToken');
  const phone = localStorage.getItem('contactNo');
  const storedLoginMethod = localStorage.getItem('loginMethod') || 'phone';
  const storedEmail = localStorage.getItem('emailAddress');

  // Determine the correct identifier (email or phone) based on login method
  const identifier = storedLoginMethod === 'email' && storedEmail ? storedEmail : phone;

  try {
    // Call logout API with correct identifier
    if (sessionToken && identifier) {
      try {
        await axios.post(
          `${API_URL.BASE_AUTH}/rest/big-local/api/v1/auth/logout?phoneNumber=${encodeURIComponent(identifier)}&role=${'Vendor'}&accessToken=${sessionToken}`,
          null,
          { timeout: 5000 }
        );
      } catch (apiError) {
        console.error(apiError);
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    // Clear tokens and storage AFTER logout API call
    try {
      localStorage.clear();
    } catch (e) {
      console.error(e);
    }

    try {
      window.dispatchEvent(new Event('user-logout'));
    } catch (e) {
      console.error(e);
    }

    // Show error toast
    toast.error('Session expired. Please login again.');
  }
};

export default axiosInstance;
